import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  BrowserRouter,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "./features/user/userSlice";
import Navbar from "./components/Navbar";
import ProfilePage from "./pages/ProfilePage";
import CommunityForum from "./pages/CommunityForum";
import SellerUniversity from "./pages/SellerUniversity";
import LogoutPage from "./pages/LogoutPage";
import SuspireSupportNetwork from "./pages/SuspireSupportNetwork";
import CertificatePage from "./pages/CertificatePage";
import CertificationDashboard from "./pages/CertificationDashboard";
import CertificateView from "./pages/CertificateView";
import BrandVisibilityCalendar from "./pages/BrandVisibilityCalendar";
import CustomerCRM from "./pages/CustomerCRM";
import LoginPage from "./pages/LoginPage";
import PublicPaymentPage from "./pages/PublicPaymentPage";
import "./styles/main.css";
import styles from "./components/Navbar.module.css";
import SuspireVisibilityCalendar from "./pages/SuspireVisibilityCalendar";
import BrandGenAIMarketingDemo from "./pages/BrandGenAIMarketingDemo";
import DemoCompanyPage from "./components/DemoCompanyPage";
import BrandsPage from "./pages/BrandsPage";
import BrandProductsPage from "./pages/BrandProductsPage";
import BrandProductContentPage from "./pages/BrandProductContentPage";
import BrandEmailCalendarPage from "./pages/BrandEmailCalendarPage";
import InternalMessagesPage from "./pages/InternalMessagesPage";
import AIBannerSuggestionsPage from "./pages/AIBannerSuggestionsPage";
import ChatInterface from "./components/ChatInterface";
import ChatbotComponent from "./components/ChatbotComponent";
import SuggestionBubble from "./components/SuggestionBubble";
import PricingPage from "./pages/PricingPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure";
import { FEATURE_PAGE_MAP } from "./features/featurePageMap";
import PrivateRoute from "./components/PrivateRoute";
import AuthRedirector from "./components/AuthRedirector";
import OnboardingFlow from "./components/OnboardingFlow";
import MarketingSuiteAppUser from "./pages/MarketingSuiteAppUser";
import ActivityDashboard from "./pages/ActivityDashboard";
import WalletPage from "./pages/WalletPage";
import SCLandingPage from "./pages/3SClandingPage";
import SCProgressLandingPage from "./pages/3SCLandingPageB";
import SCCertifiedLandingPage from "./pages/3SCLandingPageC";
import TermsConsentModal from "./pages/3SCTerms&Conditions";
import QuestionnaireStep1 from "./pages/3SCQuestionnairePageNew";
import DocumentUploadStep2 from "./pages/3SCDocumentUpload";
import ReviewPhaseStep3 from "./pages/3SCReviewPhase";
import BrandDashboard from "./pages/BrandDashboard";
import SSUChatbotLanding from "./pages/SSULandingPage";
import Shipturtle from "./pages/Shipturtle";
import SuspireTerminalLayout from "./pages/SuspireTerminalLayout";
import ShipturtleOrders from "./pages/ShipturtleOrders";
import TerminalLabels from "./pages/TerminalLabels";
import TerminalManualOrder from "./pages/TerminalManualOrder";
import {
  TerminalB2BOrders,
  TerminalB2BOrderEdit,
  TerminalB2BOrderNew,
} from "./pages/TerminalB2BOrders";
import ShipturtleProducts from "./pages/ShipturtleProducts";
import ShipturtleVendors from "./pages/ShipturtleVendors";
import ShipturtleVendorStore from "./pages/ShipturtleVendorStore";
import TerminalFAQ from "./pages/TerminalFAQ";
import ShipturtleVendorProductDetail from "./pages/ShipturtleVendorProductDetail";
import OrderDetails from "./pages/OrderDetails";
import TerminalDashboard from "./pages/TerminalDashboard";
import FallbackGallery from "./pages/FallbackGallery";
import NotFound from "./pages/NotFound";
import EditOnboardingFormPage from "./pages/EditOnboardingFormPage";
import SupportTicketsList from "./pages/SupportTicketsList";
import CreateTicket from "./pages/CreateTicket";
import TicketDetails from "./pages/TicketDetails";

// 3SC Protected Route Component
function Suspire3SCRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Prevent double fetch in StrictMode/dev by using a ref flag
  const hasCheckedStatus = React.useRef(false);
  useEffect(() => {
    if (hasCheckedStatus.current) return;
    hasCheckedStatus.current = true;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        const url =
          process.env.REACT_APP_API_BASE_URL +
          "/api/threesc/submission-status/";
        let res, data, errorData;
        try {
          res = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
        } catch (fetchErr) {
          console.error(
            "Network error while fetching submission status:",
            fetchErr,
          );
          setLoading(false);
          return;
        }

        if (res.status === 401) {
          navigate("/login");
          return;
        }

        if (!res.ok) {
          try {
            errorData = await res.json();
          } catch (e) {
            errorData = null;
            console.error("Error parsing error response:", e);
          }
          if (
            res.status === 404 ||
            (errorData && errorData.detail === "No active submission found.")
          ) {
            navigate("/sustainability");
            return;
          }
          console.error("Unexpected error:", errorData);
          return;
        }

        try {
          data = await res.json();
        } catch (jsonErr) {
          console.error("Error parsing submission status JSON:", jsonErr);
          setLoading(false);
          return;
        }
        console.log(data);
        const currentPath = window.location.pathname;

        // Determine which page the user should be on based on status
        let correctPath;
        // Give first preference to progress_status if present and is a pending
        if (data.progress_status === "Questionnaire Pending") {
          correctPath = "/sustainability/questionnaire";
        } else if (data.progress_status === "Document Upload Pending") {
          correctPath = "/sustainability/document-upload";
        } else if (data.status === "Pending") {
          correctPath = "/sustainability/review";
        } else if (data.status === "Active") {
          correctPath = "/sustainability-certified";
        } else if (data.status === "Expired") {
          correctPath = "/sustainability-progress";
        } else {
          correctPath = "/sustainability";
        }
        console.log(
          "3SC status:",
          data.status,
          "progress_status:",
          data.progress_status,
          "-> Navigating to:",
          correctPath,
        );

        // Only navigate if user is not already on the correct path
        if (currentPath !== correctPath) {
          navigate(correctPath, { replace: true });
        }
      } catch (err) {
        console.log("Error fetching 3SC submission status:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return <div>Loading...</div>; // You can replace this with a proper loading spinner
  }

  return children;
}

// Separate component that uses useLocation inside BrowserRouter
function AppContent() {
  // State to control right panel open/close
  const [rightPanelOpen, setRightPanelOpen] = useState(false); // Always closed by default
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State for floating Tara widget
  const [taraOpen, setTaraOpen] = useState(false);

  // State to store page data for Tara
  const [pageData, setPageData] = useState(null);

  // State for session expired popup
  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);

  // Handler to open right panel (to be passed to Navbar)
  const handleOpenRightPanel = () => setRightPanelOpen(true);
  // Handler to close right panel (from inside panel)
  const handleCloseRightPanel = () => setRightPanelOpen(false);

  // Check if current route is onboarding
  const isOnboardingRoute = location.pathname === "/onboarding";
  // Set by NotFound while it is mounted. A dead end should be a clean break —
  // the sidebar and the Tara widget around a "nothing here" page make it look
  // like a section of the app that failed to load rather than a page that does
  // not exist.
  const [isNotFoundRoute, setIsNotFoundRoute] = useState(false);
  const isAuthRoute =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/signup";
  const hasSidebar = !isOnboardingRoute && !isAuthRoute && !isNotFoundRoute;
  const SIDEBAR_WIDTH_PX = 111;
  const SIDEBAR_LEFT_OFFSET_VW = 1.5;
  const SIDEBAR_GUTTER_PX = 16;
  const sidebarOffsetExpr = `calc(${SIDEBAR_WIDTH_PX}px + ${SIDEBAR_LEFT_OFFSET_VW}vw + ${SIDEBAR_GUTTER_PX}px)`;

  // Listen for page data updates from child components
  useEffect(() => {
    const handlePageDataUpdate = (event) => {
      console.log("[App] Received page data update:", event.detail);
      setPageData(event.detail);
    };

    window.addEventListener("tara-page-data-update", handlePageDataUpdate);

    return () => {
      window.removeEventListener("tara-page-data-update", handlePageDataUpdate);
    };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('[APP] sessionExpired event received');
      setShowSessionExpiredPopup((prev) => {
        if (prev) {
          console.log('[APP] Popup already showing, ignoring duplicate event');
          return prev;
        }
        console.log('[APP] Showing session expired popup');
        return true;
      });
    };

    window.addEventListener("sessionExpired", handleSessionExpired);
    console.log('[APP] sessionExpired event listener added');

    return () => {
      window.removeEventListener("sessionExpired", handleSessionExpired);
      console.log('[APP] sessionExpired event listener removed');
    };
  }, []);

  const handleGoToLogin = () => {
    console.log('[APP] Go to Login button clicked - clearing tokens and redirecting');
    
    dispatch(logout());

    setShowSessionExpiredPopup(false);
    
    window.dispatchEvent(new CustomEvent('resetSessionExpiredFlag'));
    
    navigate("/login", { replace: true });
  };

  return (
    <>
      <AuthRedirector />
      {/* Conditionally render Navbar - hide it on onboarding route */}
      {!isOnboardingRoute && !isNotFoundRoute && (
        <Navbar onOpenChatPanel={handleOpenRightPanel} />
      )}
      <div style={{ display: "flex", width: "100%" }}>
        <div
          className={styles.mainContent}
          style={{
            flex: "1 1 0",
            minWidth: 0,
            maxWidth: rightPanelOpen
              ? hasSidebar
                ? `calc(100vw - ${SIDEBAR_WIDTH_PX}px - ${SIDEBAR_LEFT_OFFSET_VW}vw - ${SIDEBAR_GUTTER_PX}px - 400px)`
                : "calc(100vw - 400px)"
              : hasSidebar
                ? `calc(100vw - ${SIDEBAR_WIDTH_PX}px - ${SIDEBAR_LEFT_OFFSET_VW}vw - ${SIDEBAR_GUTTER_PX}px)`
                : "100vw",
            width: rightPanelOpen
              ? hasSidebar
                ? `calc(100vw - ${SIDEBAR_WIDTH_PX}px - ${SIDEBAR_LEFT_OFFSET_VW}vw - ${SIDEBAR_GUTTER_PX}px - 400px)`
                : "calc(100vw - 400px)"
              : hasSidebar
                ? `calc(100vw - ${SIDEBAR_WIDTH_PX}px - ${SIDEBAR_LEFT_OFFSET_VW}vw - ${SIDEBAR_GUTTER_PX}px)`
                : "100vw",
            marginLeft: hasSidebar ? sidebarOffsetExpr : 0,
            // marginRight: 0,
            height: isOnboardingRoute ? "100vh" : "100vh",
            // marginTop: isOnboardingRoute || location.pathname === '/' || location.pathname === '/login' ? 0 : '60px', // Account for navbar height when present
            // marginTop: 0,
            boxSizing: "border-box",
            overflow: "auto",
            transition: "max-width 0.3s, width 0.3s, margin-left 0.3s",
            // background: !isOnboardingRoute ? 'url("../assets/page-background.png") no-repeat center center fixed' : 'none',
            backgroundSize: !isOnboardingRoute ? "cover" : "initial",
            // padding: isOnboardingRoute ? '0' : '20px',
            // React's style prop ignores `!important`, and the padding is 0 in
            // every branch, so use a plain 0.
            padding: 0,
          }}
        >
          <Routes>
            {/* Authentication Routes */}
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<LoginPage />} />
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <OnboardingFlow />
                </PrivateRoute>
              }
            />
            <Route
              path="/ssu-chatbot"
              element={
                <PrivateRoute>
                  <SSUChatbotLanding />
                </PrivateRoute>
              }
            />

            {/* 3SC Main Status-Protected Routes */}
            <Route
              path="/sustainability"
              element={
                <PrivateRoute>
                  <Suspire3SCRoute>
                    <SCLandingPage />
                  </Suspire3SCRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/sustainability-progress"
              element={
                <PrivateRoute>
                  <Suspire3SCRoute>
                    <SCProgressLandingPage />
                  </Suspire3SCRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/sustainability-certified"
              element={
                <PrivateRoute>
                  <Suspire3SCRoute>
                    <SCCertifiedLandingPage />
                  </Suspire3SCRoute>
                </PrivateRoute>
              }
            />

            {/* 3SC Process Routes */}
            <Route
              path="/sustainability/terms"
              element={
                <PrivateRoute>
                  <TermsConsentModal />
                </PrivateRoute>
              }
            />
            <Route
              path="/sustainability/questionnaire"
              element={
                <PrivateRoute>
                  <QuestionnaireStep1 />
                </PrivateRoute>
              }
            />
            <Route
              path="/sustainability/document-upload"
              element={
                <PrivateRoute>
                  <DocumentUploadStep2 />
                </PrivateRoute>
              }
            />
            <Route
              path="/sustainability/review"
              element={
                <PrivateRoute>
                  <ReviewPhaseStep3 />
                </PrivateRoute>
              }
            />
            <Route
              path="/brand-dashboard"
              element={
                <PrivateRoute>
                  <BrandDashboard />
                </PrivateRoute>
              }
            />

            {/* PUBLIC ROUTE - No authentication required */}
            <Route path="/pay/:linkToken" element={<PublicPaymentPage />} />

            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              }
            />
            {/* Explicit wallet route gated by marketing_suite feature */}
            <Route
              path="/wallet"
              element={
                <PrivateRoute featureCode="GROWTH_SUITE">
                  <WalletPage />
                </PrivateRoute>
              }
            />
            {/* Dynamically render feature-protected routes */}
            {Object.entries(FEATURE_PAGE_MAP).map(([featureCode, page]) => {
              if (featureCode === "SUSPIRE_TERMINAL" || featureCode === "SUPPORT_TICKETS" || featureCode === "INTERNAL_MESSAGES") {
                return null;
              }

              const PageComponent = {
                BrandsPage,
                CustomerCRM,
                BrandGenAIMarketingDemo,
                SuspireVisibilityCalendar,
                DemoCompanyPage,
                SellerUniversity,
                InternalMessagesPage,
                BrandProductContentPage,
                CommunityForum,
                SuspireSupportNetwork,
                CertificatePage,
                CertificationDashboard,
                BrandVisibilityCalendar,
                BrandEmailCalendarPage,
                AIBannerSuggestionsPage,
                MarketingSuiteAppUser,
                ActivityDashboard,
                WalletPage,
                Shipturtle,
                EditOnboardingFormPage,
                SupportTicketsList,
              }[page.component];
              if (!PageComponent) return null;

              return (
                <Route
                  key={featureCode}
                  path={page.route}
                  element={
                    <PrivateRoute featureCode={featureCode}>
                      <PageComponent />
                    </PrivateRoute>
                  }
                />
              );
            })}
            <Route
              path="/suspire-terminal"
              element={
                <PrivateRoute>
                  <SuspireTerminalLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<TerminalDashboard />} />
              <Route path="orders" element={<ShipturtleOrders />} />
              <Route path="orders/:orderId" element={<OrderDetails />} />
              <Route path="labels" element={<TerminalLabels />} />
              <Route path="manual-orders/new" element={<TerminalManualOrder />} />
              {/* B2B orders. The detail view is a modal over the list, so only
                  the list and the create/edit wizard are routed. */}
              <Route path="b2b-orders" element={<TerminalB2BOrders />} />
              <Route path="b2b-orders/new" element={<TerminalB2BOrderNew />} />
              <Route path="b2b-orders/:orderId/edit" element={<TerminalB2BOrderEdit />} />
              {/* Products owns its sub-routes (manage/pending/unlisted/add and
                  their :productId children) so the list stays mounted while a
                  product is open. See ShipturtleProducts/productsNavigation.js. */}
              <Route path="products/*" element={<ShipturtleProducts />} />
              <Route path="vendors" element={<ShipturtleVendors />} />
              <Route path="vendor-store" element={<ShipturtleVendorStore />} />
              <Route path="faq" element={<TerminalFAQ />} />
              <Route
                path="vendor-store/product/:productId"
                element={<ShipturtleVendorProductDetail />}
              />
            </Route>
            {/* Other routes not protected by features */}
            <Route
              path="/brands/:brandId/products"
              element={
                <PrivateRoute featureCode="BRAND_MANAGEMENT">
                  <BrandProductsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/brands/:brandId/email-calendar"
              element={<BrandEmailCalendarPage />}
            />
            <Route
              path="/certificate-view/:certificateId"
              element={
                <PrivateRoute featureCode="3SC_CERTIFICATION">
                  <CertificateView />
                </PrivateRoute>
              }
            />
            <Route path="/logout" element={<LogoutPage />} />
            {/* Single canonical subscriptions / plans & pricing page.
                All aliases redirect here so there is only one real route. */}
            <Route
              path="/subscriptions"
              element={
                <PrivateRoute>
                  <PricingPage />
                </PrivateRoute>
              }
            />
            <Route path="/pricing" element={<Navigate to="/subscriptions" replace />} />
            <Route path="/plans_and_pricing" element={<Navigate to="/subscriptions" replace />} />
            {/* Payment result pages - now public! */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failure" element={<PaymentFailure />} />

            {/* <Route path="/suspire-support-network" element={<SSNPage />} /> */}
            <Route
              path="/suspire-support-network"
              element={<SuspireSupportNetwork />}
            />
            {/* Suspire Helpdesk — Support Tickets */}
            <Route
              path="/suspire-helpdesk"
              element={
                <PrivateRoute featureCode="SUPPORT_TICKETS">
                  <SupportTicketsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/suspire-helpdesk/create"
              element={
                <PrivateRoute>
                  <CreateTicket />
                </PrivateRoute>
              }
            />
            <Route
              path="/suspire-helpdesk/tickets/:ticketId"
              element={
                <PrivateRoute>
                  <TicketDetails />
                </PrivateRoute>
              }
            />

            {/* Every loading and fallback state on one page, for reviewing copy
                and sizing without provoking each failure. Never built into a
                production bundle. */}
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/dev/fallbacks" element={<FallbackGallery />} />
            )}

            {/*
              A mistyped or stale link used to redirect silently to `/`, which
              is disorienting: a bookmark that had moved looked like a login
              loop, and nothing explained what happened.
            */}
            <Route path="*" element={<NotFound onMount={setIsNotFoundRoute} />} />
          </Routes>
        </div>
        {/* Only show right panel if not on login page or onboarding page */}
        {rightPanelOpen &&
          !isOnboardingRoute &&
          !isAuthRoute &&
          !isNotFoundRoute && (
            <div
              style={{
                width: "25vw",
                minWidth: 260,
                maxWidth: 540,
                background: "#fff",
                borderLeft: "1px solid #e0e0e0",
                boxShadow: "-2px 0 8px rgba(60,60,90,0.04)",
                height: "calc(100vh - 60px)",
                position: "fixed",
                right: 0,
                top: 60,
                zIndex: 1201,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                padding: 0,
                transition: "width 0.3s",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#004443",
                  color: "#fff",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  padding: "1rem 1.2rem",
                  borderRadius: "0 0 12px 12px",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  caretColor: "transparent",
                }}
              >
                <span>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    style={{ verticalAlign: "middle", marginRight: 6 }}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="6"
                      width="16"
                      height="8"
                      rx="4"
                      fill="#00bfae"
                    />
                    <circle cx="8" cy="10" r="1" fill="#fff" />
                    <circle cx="14" cy="10" r="1" fill="#fff" />
                    <path
                      d="M17 4.5l.5-1.2.5 1.2 1.2.5-1.2.5-.5 1.2-.5-1.2-1.2-.5 1.2-.5z"
                      fill="#FFD600"
                    />
                  </svg>
                  Chat with AI Agent
                </span>
                <button
                  onClick={handleCloseRightPanel}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: "2rem",
                    cursor: "pointer",
                    marginLeft: "1rem",
                    lineHeight: 1,
                    padding: "0 0.3rem",
                    borderRadius: 6,
                    transition: "background 0.2s",
                  }}
                  title="Close chat"
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  flex: "1 1 0",
                  minHeight: 0,
                  overflowY: "auto",
                  background: "#fff",
                  height: "100%",
                }}
              >
                {/* Pass recommendations and page context if needed */}
                <ChatInterface />
              </div>
            </div>
          )}
      </div>
      {/* Floating Tara widget, always visible except on onboarding/login/seller-university
          and on a dead end, where an assistant offering suggestions about a page
          that does not exist is just noise. */}
      {location.pathname !== "/onboarding" &&
        !isAuthRoute &&
        !isNotFoundRoute &&
        !location.pathname.startsWith("/seller-university") &&
        !location.pathname.startsWith("/ssu-chatbot") && (
          <>
            {/* Floating container for Tara widget components */}
            {!taraOpen && (
              <div
                style={{
                  position: "fixed",
                  // Tara at the bottom; Support stacked above
                  bottom: 32,
                  right: 32,
                  zIndex: 2000,
                  width: 69,
                  height: 69,
                }}
              >
                {/* Suggestion bubble next to chatbot button (only when chatbot is closed) */}
                <SuggestionBubble onClick={() => setTaraOpen(true)} />
                <button
                  onClick={() => setTaraOpen(true)}
                  style={{
                    position: "relative",
                    background:
                      "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "100%",
                    height: "100%",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "background 0.2s",
                    padding: 0,
                  }}
                  title="Chat with Tara"
                >
                  <img
                    src="/images/tara-avatar.png"
                    alt="Tara"
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      objectFit: "cover",
                      objectPosition: "center 22%",
                      display: "block",
                    }}
                  />
                </button>
              </div>
            )}
            {/* Tara chat widget popup */}
            {taraOpen && (
              <div
                style={{
                  position: "fixed",
                  bottom: 32,
                  right: 32,
                  zIndex: 2100,
                  padding: 0,
                  width: "min(520px, 92vw)", // 30% wider than previous 400px
                  maxHeight: "min(820px, 90vh)", // 20% taller than previous 680px
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  animation:
                    "tara-fade-up 0.35s cubic-bezier(0.25, 0.8, 0.25, 1) forwards",
                  transformOrigin: "bottom right",
                }}
              >
                <style>
                  {`
                    @keyframes tara-fade-up {
                      from {
                        opacity: 0;
                        transform: scale(0.85) translateY(20px);
                      }
                      to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                      }
                    }
                    .hide-scrollbar::-webkit-scrollbar {
                      display: none; /* Chrome/Safari/Opera */
                    }
                  `}
                </style>
                <div
                  className="hide-scrollbar"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "hidden", // Prevents full-widget scrolling, passes it down to ChatbotComponent
                    background: "transparent",
                  }}
                >
                  <ChatbotComponent
                    pageData={pageData}
                    onClose={() => setTaraOpen(false)}
                  />
                </div>
              </div>
            )}
          </>
        )}

      {/* Global Session Expired Popup */}
      {showSessionExpiredPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onKeyDown={(e) => {
            // Prevent Escape key from closing
            if (e.key === "Escape") {
              e.stopPropagation();
            }
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              padding: "2rem",
              textAlign: "center",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "1rem",
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#1a3c34",
              }}
            >
              Session Expired
            </h3>
            <p
              style={{
                marginBottom: "1.5rem",
                fontSize: "1rem",
                color: "#555",
                lineHeight: "1.6",
              }}
            >
              Your session has expired due to inactivity. Please log in again to continue.
            </p>
            <button
              type="button"
              onClick={handleGoToLogin}
              style={{
                background: "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "12px 32px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 12px rgba(0, 191, 174, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 16px rgba(0, 191, 174, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 191, 174, 0.3)";
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
